import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import { UploadIcon } from '../../../components/icons';
import ExplainerImportModal from './ExplainerImportModal';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import RichContent, { normalizeRichText, richTextIsEmpty } from '../../../components/RichContent';
import AdminModal from '../../components/AdminModal';
import { WarningIcon } from '../../../components/icons';
import {
  CITIZEN_CONTENT_FIELDS, CONTENT_ORIGINS, isBallotVisible, isPublished, provenanceLabel, statusLabel,
} from '../../../constants/ballotInitiatives';
import type { CitizenContentField, DeclarableField, ProvenanceEntry } from '../../../constants/ballotInitiatives';
import { formatDate } from '../../lib/format';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

type Provenance = Partial<Record<DeclarableField, ProvenanceEntry>>;

interface Props {
  initiativeId: string;
  /** The measure's canonical status — gates the citizens-can't-see-this warning. */
  status: string;
  /** Editorial release state — the other half of that warning (PRD v2 §5.5.4). */
  publicationStatus: string;
  /** Opens the page's Publish confirmation; the warning offers it inline. */
  onPublish?: () => void;
  /** The ballot-visible record this measure probably duplicates — named and linked in the warning. */
  twin?: { id: string; label: string; status: string } | null;
  values: Record<CitizenContentField, string | null>;
  provenance: Provenance | null;
}

const FIELD_LABELS: Record<CitizenContentField, string> = {
  plain_language_summary: 'Plain-Language Summary',
  plain_language_long_description: 'Plain-Language Long Description',
  what_yes_means: 'What a YES vote means',
  what_no_means: 'What a NO vote means',
  who_supports_and_why: 'Who Supports It and Why',
  who_opposes_and_why: 'Who Opposes It and Why',
  background_context: 'Background — what the law does today',
};

const PLACEHOLDERS: Record<CitizenContentField, string> = {
  plain_language_summary: 'Citizen-friendly summary, distinct from the official ballot title',
  plain_language_long_description: 'The fuller explanation a reader opens from the summary — what the measure changes, in order, still in plain language',
  what_yes_means: 'A yes vote on this measure means…',
  what_no_means: 'A no vote on this measure means…',
  who_supports_and_why: 'Who is for it and the case they make, in the reader\'s words — not a list of endorsements',
  who_opposes_and_why: 'Who is against it and the case they make, in the reader\'s words',
  background_context: 'What current law does, before this measure',
};

/**
 * Citizen Content — the four fields a voter actually reads, each carrying a required
 * origin declaration.
 *
 * Drafting happens outside the product, so nothing in a save distinguishes pasted AI
 * text from typed text; the admin asserts it. Making the assertion mandatory is what
 * gives an unlabelled field meaning — it is one a named person affirmatively said they
 * wrote, not one nobody got round to marking.
 */
const InitiativeCitizenContentCard: React.FC<Props> = ({
  initiativeId, status, publicationStatus, onPublish, twin, values, provenance,
}) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [text, setText] = useState<Record<string, string>>({});
  const [origins, setOrigins] = useState<Record<string, string>>({});
  const [details, setDetails] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const openEdit = () => {
    const nextText: Record<string, string> = {};
    const nextOrigins: Record<string, string> = {};
    const nextDetails: Record<string, string> = {};
    for (const field of CITIZEN_CONTENT_FIELDS) {
      nextText[field] = values[field] ?? '';
      const entry = provenance?.[field];
      // No fallback default: an unset field opens with nothing selected, on purpose.
      nextOrigins[field] = entry?.origin ?? '';
      nextDetails[field] = entry?.tool ?? entry?.source ?? '';
    }
    setText(nextText);
    setOrigins(nextOrigins);
    setDetails(nextDetails);
    setError(null);
    setOpen(true);
  };

  const undeclared = CITIZEN_CONTENT_FIELDS.filter(
    (field) => !richTextIsEmpty(text[field]) && !origins[field]
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      const patch: Record<string, unknown> = {};
      for (const field of CITIZEN_CONTENT_FIELDS) {
        const value = normalizeRichText(text[field]);
        body[field] = value || null;
        if (!value) { patch[field] = null; continue; }
        const origin = origins[field];
        const entry: Record<string, string> = { origin };
        if (origin === 'ai_reviewed' && details[field]?.trim()) entry.tool = details[field].trim();
        if (origin === 'official_source' && details[field]?.trim()) entry.source = details[field].trim();
        patch[field] = entry;
      }
      body.content_provenance = patch;
      return api.put(`/admin/ballot-initiatives/${initiativeId}`, body);
    },
    onSuccess: () => {
      toast.success('Citizen content saved');
      setOpen(false);
      invalidateInitiativeQueries(queryClient, initiativeId);
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save citizen content')),
  });

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Citizen Content
        </h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" icon={UploadIcon} onClick={() => setImportOpen(true)}>Import explainer</Button>
          <Button variant="ghost" size="sm" icon={Pencil} onClick={openEdit}>Edit</Button>
        </div>
      </div>

      <ExplainerImportModal
        initiativeId={initiativeId}
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => invalidateInitiativeQueries(queryClient, initiativeId)}
      />

      {!isBallotVisible(status) ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
          <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Citizens can&apos;t see this measure — its status is {statusLabel(status)}, and only
            qualified or on-ballot measures reach the citizen ballot. Content entered here stays
            invisible until the status changes.{' '}
            {twin ? (
              <>
                The on-ballot record for this measure is{' '}
                <strong>{twin.label}</strong> ({statusLabel(twin.status)}) —{' '}
                <Link to={`/admin-next/elections/initiatives/${twin.id}`} className="font-medium underline">
                  open it
                </Link>
                .
              </>
            ) : (
              <>
                If you meant a measure that <em>is</em> on the ballot, open that measure&apos;s
                own record instead of editing this one.
              </>
            )}
          </p>
        </div>
      ) : !isPublished(publicationStatus) && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
          <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <p>
              Citizens can&apos;t see this measure yet — it hasn&apos;t been published. Content saved
              here goes live the moment a system admin publishes it.
            </p>
            {onPublish && (
              <Button variant="primary" size="sm" onClick={onPublish}>Publish</Button>
            )}
          </div>
        </div>
      )}

      <dl className="space-y-4">
        {CITIZEN_CONTENT_FIELDS.map((field) => {
          const value = values[field];
          const label = provenanceLabel(provenance?.[field]);
          const reviewedAt = provenance?.[field]?.reviewed_at;
          return (
            <div key={field}>
              <dt className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                {FIELD_LABELS[field]}
              </dt>
              <dd className="mt-0.5 text-sm text-text-primary dark:text-text-dark-primary">
                {value ? (
                  <RichContent html={value} className="text-sm text-text-primary dark:text-text-dark-primary" />
                ) : (
                  <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>
                )}
              </dd>
              {label && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {label}
                  {reviewedAt && ` · reviewed ${formatDate(reviewedAt)}`}
                </p>
              )}
            </div>
          );
        })}
      </dl>

      <AdminModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Edit Citizen Content"
        size="lg"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={undeclared.length > 0 || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>}
      >
        <div className="space-y-6">
          {!isBallotVisible(status) ? (
            <p className="flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
              <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This measure&apos;s status is {statusLabel(status)} — citizens won&apos;t see
                anything you save here.
              </span>
            </p>
          ) : !isPublished(publicationStatus) && (
            <p className="flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
              <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This measure isn&apos;t published yet — what you save here goes live when it is.
              </span>
            </p>
          )}
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            The origin you choose becomes a public label — an AI-assisted or official-source
            field is marked as such wherever citizens read it, and an unlabelled field is a
            claim that our team wrote it.
          </p>
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}

          {undeclared.length > 0 && (
            <p className="rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
              Choose where the text came from for:{' '}
              {undeclared.map((f) => FIELD_LABELS[f]).join(', ')}.
            </p>
          )}

          {CITIZEN_CONTENT_FIELDS.map((field) => {
            const filled = !richTextIsEmpty(text[field]);
            const origin = origins[field];
            return (
              <div key={field} className="border-b border-border-light pb-5 last:border-b-0 dark:border-border-dark">
                <span className={labelClasses}>{FIELD_LABELS[field]}</span>
                <QuillEditor
                  value={text[field] ?? ''}
                  onChange={(value) => setText((c) => ({ ...c, [field]: value }))}
                  placeholder={PLACEHOLDERS[field]}
                />

                {filled && (
                  <fieldset className="mt-3">
                    <legend className="mb-1.5 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      Where did this text come from? *
                    </legend>
                    <div className="space-y-1.5">
                      {CONTENT_ORIGINS.map((option) => (
                        <label key={option.value} className="flex items-start gap-2 text-sm">
                          <input
                            type="radio"
                            name={`origin-${field}`}
                            value={option.value}
                            checked={origin === option.value}
                            onChange={() => setOrigins((c) => ({ ...c, [field]: option.value }))}
                            className="mt-1"
                          />
                          <span>
                            <span className="text-text-primary dark:text-text-dark-primary">{option.label}</span>
                            <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                              {option.hint}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>

                    {(origin === 'ai_reviewed' || origin === 'official_source') && (
                      <div className="mt-2">
                        <label htmlFor={`detail-${field}`} className={labelClasses}>
                          {origin === 'ai_reviewed' ? 'Tool used' : 'Source'}
                        </label>
                        <input
                          id={`detail-${field}`}
                          type="text"
                          maxLength={120}
                          value={details[field] ?? ''}
                          onChange={(e) => setDetails((c) => ({ ...c, [field]: e.target.value }))}
                          className={inputClasses}
                          placeholder={origin === 'ai_reviewed' ? 'e.g. Claude Opus 5' : 'e.g. 2026 Blue Book, p. 12'}
                        />
                      </div>
                    )}
                  </fieldset>
                )}
              </div>
            );
          })}
        </div>
      </AdminModal>
    </div>
  );
};

export default InitiativeCitizenContentCard;
